import { useRef, useState } from 'react'
import type { JSX, SyntheticEvent } from 'react'
import { Link } from 'react-router'
import { api, call } from '@/api/client'
import type { Schema } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import type { Query } from '@/api/query'
import { formatClock, formatDateIn, formatOpening } from '@/lib/format'
import { summariseDiscoveryRules } from '@/settings/discovery-rules/summary'
import { may, useActiveTenant } from '@/tenant/context'
import { Button } from '@/ui/Button'
import { Field } from '@/ui/Field'
import { Empty, ErrorState, InlineError, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'

type Keyword = Schema<'OutreachKeywordOut'>
type Pack = Schema<'KeywordPackOut'>
type Filter = Schema<'DiscoveryFilterOut'>
type LastRun = Schema<'DiscoveryLastRunOut'>
type Suggestion = Schema<'PackSuggestionOut'>
type Discovery = Schema<'DiscoveryStatusResponse'>

/** The tenant's time zone is the only one this product renders in (CLAUDE.md). */
const IST = 'Asia/Kolkata'

/**
 * `NEAR_LIMIT_FRACTION` in `app/outreach/health.py`, mirrored.
 *
 * Duplicated rather than served, for the reason `FIRST_PAGE` in `Suppression.tsx`
 * is: it is a presentation threshold and nothing refuses at 80%, so putting it on
 * the wire would be adding a field to `DiscoveryStatusResponse` that only ever
 * carries the same constant. The account-health meter next door colours on the
 * server's own `near_limit`, so the two agree by construction as long as this
 * number matches — which is why it is named and commented rather than inlined.
 */
const NEAR_LIMIT = 0.8

/**
 * "What we search for" — `design/screens/outreach.html#packs`, audit row §B5,
 * the last open row in `docs/MOCKUP-GAP-AUDIT.md`.
 *
 * Four things behind one screen, and the API answers all four in one `GET`: the
 * organisation's own keywords, the curated packs it can start from, the post
 * filter that decides what counts as a lead, and what the last run did with them.
 *
 * THIS IS A POST SEARCH, AND THE SCREEN SAYS SO
 * -----------------------------------------------
 * `docs/DESIGN-AUDIT.md` §4.1 records the most expensive defect the design audit
 * found: this screen was *"Who we're looking for"* with job titles, a Locations
 * field and a Company-size dropdown — which is LinkedIn *people* search. §5 and
 * the ported `../linkmailer/scraper.py` both search *posts*: each keyword is run
 * against post results sorted by latest, recent ones are kept, and the lead is
 * whoever wrote one. None of those three controls exists in post search, so none
 * of them is here and none of them is coming back. The controls that are here are
 * the ones post search actually has — keywords, an age window, and phrases that
 * disqualify a post.
 *
 * WHERE THE LAST-RUN PANEL DEVIATES FROM THE PROTOTYPE, AND WHY
 * ---------------------------------------------------------------
 * The prototype draws five rows and this draws six. The extra one is **No address
 * in the post**, which `app/outreach/posts.read_page` calls the commonest drop of
 * all — and without it the rows do not sum to *Posts read*, which is the single
 * fastest way to make somebody stop believing a funnel. *Found under another
 * keyword* is here for the same arithmetic: it is neither a filter nor a fault,
 * just the same post surfacing twice.
 *
 * The heading is *Latest run* with a date rather than the prototype's
 * *Last run — 04:30 IST*: `discovery_pages` carries no run identifier, so
 * `app/db/packs.LastRun` defines a run as one IST day's filed pages and the
 * heading says which day rather than implying a precision the table cannot back.
 *
 * WHY THE PACK CARDS CARRY NO "~180 POSTS A DAY"
 * ------------------------------------------------
 * The prototype's cards read *"34 keywords · 61 exclusions · ~180 posts a day"*.
 * The first two are real and rendered. The third is a forecast of what LinkedIn
 * will return for terms this tenant has never run, and there is nothing in
 * AugRank that could compute it — it would be an invented number on the screen a
 * tenant uses to decide where their scrape budget goes. This is the same call
 * §B3 made about the prototype's *"₹8.4L in play"*.
 */
export function NichePacks(): JSX.Element {
  const { membership } = useActiveTenant()
  const manageable = may(membership, 'discovery:manage')
  const screen = useQuery(
    (signal) => call(() => api.GET('/api/v1/outreach/keywords', { signal })),
    [],
  )
  const enabledKeywords =
    screen.state.status === 'ready'
      ? screen.state.data.keywords.filter((keyword) => keyword.enabled).length
      : 0

  return (
    <div className="ar-stack ar-stack--loose">
      <div className="ar-row">
        <h1 className="ar-h1">What we search for</h1>
        <span className="app-spacer" />
        {/* OUTR-85. Only for a role that may manage discovery, because the route
            behind it is `discovery:manage` — a member would get a 403 rendered
            as a broken badge, which is the boundary looking like a fault. */}
        {manageable ? <DiscoveryControls enabledKeywords={enabledKeywords} /> : null}
      </div>

      {screen.state.status === 'loading' ? (
        <Loading label="Loading your keywords" lines={6} />
      ) : null}
      {screen.state.status === 'error' ? (
        <ErrorState
          failure={screen.state.failure}
          onRetry={screen.refetch}
          title="Could not load your keywords"
        />
      ) : null}
      {screen.state.status === 'ready' ? (
        <div className="ar-grid ar-grid--2">
          <div className="ar-stack">
            <KeywordsCard
              keywords={screen.state.data.keywords}
              suggestions={screen.state.data.suggestions}
              replyKeywords={screen.state.data.reply_keywords}
              manageable={manageable}
              onChanged={screen.refetch}
            />
            <FilterReadout filter={screen.state.data.settings} />
          </div>
          <div className="ar-stack">
            <PacksCard
              packs={screen.state.data.packs}
              manageable={manageable}
              onChanged={screen.refetch}
            />
            <LastRunCard run={screen.state.data.last_run} />
          </div>
        </div>
      ) : null}
    </div>
  )
}

/**
 * OUTR-85 — *"31 of 50 searches used today"*, in the page header.
 *
 * **The same counter the account-health panel meters, not a second one.** Both
 * read `Observed.searches_today` for this tenant's LinkedIn account through
 * `SEARCH_POLICY`, which is also what the claim endpoint decides on — so this
 * badge cannot say *31 of 50* while the browser is being refused for a spent
 * budget. It is on *this* screen because this is where a tenant adds the
 * keywords that spend it: the cap is per day and per organisation, so the tenth
 * keyword does not get its own fifty searches, and the header is where that
 * arithmetic has to be visible before the *Add* button is pressed.
 *
 * **The tones are `health.NEAR_LIMIT_FRACTION`'s, not new ones.** Warning at 80%
 * because that is where *"you will run out today"* stops being alarmist, danger
 * once the allowance is spent. Below 80% it is neutral — the figure is still
 * worth reading, the badge simply does not shout it.
 *
 * **A tenant with no LinkedIn connected gets a different sentence, not a zero.**
 * `searches_today: 0` against a cap of fifty is arithmetically true and reads as
 * *discovery ran today and found nothing to do*, which is the invented-number
 * failure §A3's *30-day uptime* was closed for. Nothing has been spent because
 * nothing can be.
 *
 * **A failed load says so.** One `GET` failing does not stop the screen — the
 * keywords are a separate request with its own error state — but a badge that
 * silently vanished would leave a tenant reading the rest of the page as though
 * they had budget nobody had checked.
 */
/**
 * The allowance badge and the button that spends it, over one status read.
 *
 * One component rather than two siblings because the button changes the
 * badge's answer (`queued`), so they share the query and its refetch — and
 * because the read is `discovery:manage`'s, which is why the whole thing is
 * mounted only for a role that holds it (OUTR-85: a member gets no request,
 * not a 403 drawn as a broken badge).
 *
 * **The button is the one that was missing (2026-09-12).** `POST
 * /outreach/discovery/runs` is the only producer of the extension's queue, and
 * nothing in the product called it — no button, no schedule — so a tenant with
 * LinkedIn connected, a browser paired and keywords added watched *Nothing
 * searched yet* indefinitely (found on the live tenant). The extension polls
 * for work; this is what gives it some.
 */
function DiscoveryControls({ enabledKeywords }: { enabledKeywords: number }): JSX.Element {
  const { tenant } = useActiveTenant()
  const discovery = useQuery(
    (signal) => call(() => api.GET('/api/v1/outreach/discovery', { signal })),
    [],
  )
  const window = discovery.state.data
  return (
    <>
      <AllowanceBadge status={discovery} />
      <SearchNow
        enabledKeywords={enabledKeywords}
        window={window ? { open: window.window_open, opensAt: window.window_opens_at } : null}
        // Served on the organisation, for `RunTriggers`' reason: the rule is
        // one function in `app/billing/trial.py`, and a second copy of it in
        // TypeScript is the thing that would disagree.
        entitled={tenant.automation_available}
        onQueued={() => {
          discovery.refetch()
        }}
      />
    </>
  )
}

function AllowanceBadge({ status }: { status: Query<Discovery> }): JSX.Element | null {
  if (status.state.status === 'loading') return null
  if (status.state.status === 'error') {
    return (
      <span className="ar-badge ar-badge--neutral">
        Today&rsquo;s search allowance is unavailable
      </span>
    )
  }

  const { connected, searches_today: used, max_searches_per_day: cap, queued } = status.state.data
  if (!connected) {
    return <span className="ar-badge ar-badge--neutral">LinkedIn is not connected</span>
  }

  // Subtle text, as `outreach.html#packs` draws the aside (parity P-PACK-01);
  // the tone still colours it once the allowance is nearly or fully used, a
  // state the mockup does not draw.
  const tone = used >= cap ? 'danger' : used >= cap * NEAR_LIMIT ? 'warning' : null
  return (
    <span
      className={tone === null ? 'ar-subtle' : `ar-badge ar-badge--${tone}`}
      style={tone === null ? { fontSize: 'var(--ar-text-body-sm-size)' } : undefined}
    >
      {used} of {cap} searches used today
      {/* What the button beside this just did, and what the paired browser is
          about to do — the one number that tells a tenant the queue is real. */}
      {queued > 0 ? ` · ${String(queued)} waiting to run` : ''}
    </span>
  )
}

/**
 * Queue every enabled keyword as a search for the paired browser.
 *
 * **The response is a report, not a verdict, and the toast says which case it
 * is.** The route answers 200 for *already in flight* (`created: false`) rather
 * than 409, because a double-tap is a tenant asking *is this happening* and the
 * honest answer is yes. `awaiting_browser` is the other thing worth saying out
 * loud: the searches are queued either way, and a tenant whose browser is shut
 * would otherwise watch a queue that never moves.
 *
 * **Refusals are the server's sentences.** `admit_run` names the remedy for
 * each — no connection, a paused account, too many keywords — and the plan gate
 * answers 402. `InlineError` renders those as written; nothing here re-decides
 * them. The one thing decided on the screen is the zero-keyword case, made
 * unavailable rather than sent to the server to be refused, because the remedy
 * is the field two cards down.
 */
function SearchNow({
  enabledKeywords,
  window,
  entitled,
  onQueued,
}: {
  enabledKeywords: number
  /** The send window's state, or `null` while the status is still loading. */
  window: { open: boolean; opensAt: string | null } | null
  /** `tenant.automation_available`: whether unattended work runs on this plan. */
  entitled: boolean
  onQueued: () => void
}): JSX.Element {
  const toast = useToast()
  const run = useMutation(() =>
    call(() => api.POST('/api/v1/outreach/discovery/runs', { body: { pack_slug: null } })),
  )
  // Outside the send window the server refuses — a rule, argued in
  // `app/linkedin/limits.admit_run`, not a fault. Said here, before the press,
  // instead of as a red error with a support reference after it (walkthrough
  // 2026-09-12, a Saturday).
  const shut = window !== null && !window.open
  // And on a trial the paired browser is refused every search it asks for
  // (`hand_out` → `automation_allowed`, 2026-09-10): a search is unattended
  // work, and unattended work waits for a subscription. Queueing twelve of
  // them and printing *12 waiting to run* was the product's whole answer until
  // 2026-09-12, and the only place the refusal showed was the extension's own
  // popup. Said here instead, before the press.
  const reason = !entitled
    ? 'Searches wait for a subscription — choose a plan'
    : enabledKeywords === 0
      ? 'Add a keyword first'
      : shut
        ? `Searches run inside your send window${
            window.opensAt ? ` — next from ${formatOpening(window.opensAt, IST)} IST` : ''
          }`
        : undefined
  const blocked = reason !== undefined

  return (
    <>
      <Button
        tone="primary"
        size="sm"
        busy={run.pending}
        busyLabel="Queueing"
        // Both, because `unavailable` is the *treatment* (focusable, announced)
        // and `disabled` is the *state* it applies to — see `Button`.
        disabled={blocked}
        unavailable={blocked}
        title={reason}
        onClick={() => {
          void (async () => {
            const result = await run.run(undefined)
            if (!result.ok) return
            const { created, queued, already_queued: waiting, awaiting_browser: shut } = result.data
            const browser = shut
              ? 'Nothing runs until the browser you paired is open and signed in to LinkedIn.'
              : 'Your paired browser runs them one at a time, with a pause between each.'
            toast.show(
              created
                ? {
                    kind: 'success',
                    title: `${String(queued)} ${queued === 1 ? 'search' : 'searches'} queued.`,
                    body: browser,
                  }
                : {
                    kind: 'info',
                    title: `Already queued — ${String(waiting)} ${waiting === 1 ? 'search is' : 'searches are'} waiting.`,
                    body: browser,
                  },
            )
            onQueued()
          })()
        }}
      >
        Search now
      </Button>
      {!entitled ? (
        <span className="ar-subtle ar-text-body-sm app-search-now__note">
          Your paired browser is refused searches until a plan is chosen —{' '}
          <Link to="/billing/plans">see the plans</Link>
        </span>
      ) : shut && enabledKeywords > 0 ? (
        <span className="ar-subtle ar-text-body-sm app-search-now__note">
          Next from {window.opensAt ? `${formatOpening(window.opensAt, IST)} IST` : 'the window'} —{' '}
          <Link to="/outreach/health#send-window">change the window</Link>
        </span>
      ) : null}
      {run.state.failure ? <InlineError failure={run.state.failure} takesFocus /> : null}
    </>
  )
}

function KeywordsCard({
  keywords,
  suggestions,
  replyKeywords,
  manageable,
  onChanged,
}: {
  keywords: Keyword[]
  suggestions: Suggestion[]
  replyKeywords: string[]
  manageable: boolean
  onChanged: () => void
}): JSX.Element {
  const toast = useToast()
  const [draft, setDraft] = useState('')
  const [missing, setMissing] = useState(false)
  const field = useRef<HTMLInputElement>(null)
  const toggle = useMutation((value: { id: string; enabled: boolean }) =>
    call(() =>
      api.PATCH('/api/v1/outreach/keywords/{keyword_id}', {
        params: { path: { keyword_id: value.id } },
        body: { enabled: value.enabled },
      }),
    ),
  )
  const add = useMutation((value: { keyword: string; source: 'custom' | 'suggested' }) =>
    call(() => api.POST('/api/v1/outreach/keywords', { body: value })),
  )

  async function onToggle(keyword: Keyword): Promise<void> {
    const result = await toggle.run({ id: keyword.id, enabled: !keyword.enabled })
    if (result.ok) onChanged()
  }

  async function onAdd(event: SyntheticEvent): Promise<void> {
    event.preventDefault()
    const keyword = draft.trim()
    if (!keyword) {
      setMissing(true)
      field.current?.focus()
      return
    }
    const result = await add.run({ keyword, source: 'custom' })
    if (result.ok) {
      setDraft('')
      toast.show({ kind: 'success', title: `Added “${keyword}”.` })
      onChanged()
    }
  }

  async function onAccept(suggestion: Suggestion): Promise<void> {
    const result = await add.run({ keyword: suggestion.keyword, source: 'suggested' })
    if (result.ok) {
      toast.show({ kind: 'success', title: `Added “${suggestion.keyword}”.` })
      onChanged()
    }
  }

  return (
    <div className="ar-card">
      <div className="ar-card__header">
        <h2>Your keywords</h2>
        <span className="ar-card__spacer" />
        {/*
          OUTR-86, OUTR-121. The header carried a caption reading *"N of M
          searched"* and the drawing carries an *Add*. The button wins on both
          counts.

          The caption counted this card's enabled chips, which the chips
          themselves already say — each one is `aria-pressed`, and a switched-off
          keyword is drawn as switched off. Worse, OUTR-85 has just put a
          different *N of M* in the page header, and that one is the day's search
          budget: two fractions on one screen, one of them a restatement of the
          chips below it, and the reader has to work out which is the one that
          costs money.

          The button is what the header slot is for on a card whose content is a
          list you add to, and it does the only honest thing a header *Add* can
          do when the field is at the foot — put the cursor in it. Adding is
          still the labelled field with its help text (OUTR-126); this is the
          affordance that says the card takes new entries without asking anybody
          to scroll to find out.
        */}
        {manageable ? (
          <Button
            tone="ghost"
            size="sm"
            onClick={() => {
              field.current?.focus()
            }}
          >
            Add
          </Button>
        ) : null}
      </div>
      <div className="ar-card__body ar-stack">
        {keywords.length === 0 ? (
          <Empty
            title="No keywords yet"
            body="Add a phrase a buyer would post, or start from one of the curated packs."
          />
        ) : (
          <div className="app-chips">
            {keywords.map((keyword) => (
              <button
                key={keyword.id}
                type="button"
                className="app-chip"
                aria-pressed={keyword.enabled}
                disabled={!manageable || toggle.pending}
                onClick={() => void onToggle(keyword)}
                title={
                  keyword.pack_slug
                    ? `From the ${keyword.pack_slug} pack, version ${String(keyword.pack_version ?? 1)}`
                    : 'Added by your organisation'
                }
              >
                {keyword.keyword}
              </button>
            ))}
          </div>
        )}
        <InlineError failure={toggle.state.failure} />

        <div>
          {/* OUTR-122. Body-sm with the drawing's `--ar-space-2` under it, as a
              class rather than an inline `style`: this is a label over a group,
              which is the same weight as the field labels beside it, and at
              caption size with nothing under it the chips read as belonging to
              the row above rather than to this heading. */}
          <div className="ar-muted ar-text-body-sm app-packs__suggested">Suggested for you</div>
          {suggestions.length === 0 ? (
            <span className="ar-field__help">
              {replyKeywords.length === 0
                ? 'Suggestions are drawn from the posts that produced your replies. You have not had any yet.'
                : 'Nothing new to suggest — you already have every term from the packs your replies came from.'}
            </span>
          ) : (
            <>
              <div className="app-chips">
                {suggestions.map((suggestion) => (
                  <button
                    key={suggestion.keyword}
                    type="button"
                    className="app-chip"
                    aria-pressed={false}
                    disabled={!manageable || add.pending}
                    onClick={() => void onAccept(suggestion)}
                  >
                    + {suggestion.keyword}
                  </button>
                ))}
              </div>
              <span className="ar-field__help">
                Drawn from the posts that produced your last 20 replies — the rest of the{' '}
                {suggestions[0]?.pack_name ?? 'matching'} pack.
              </span>
            </>
          )}
        </div>

        {manageable ? (
          <form className="ar-row" onSubmit={(event) => void onAdd(event)}>
            <div style={{ flex: 1 }}>
              {/* `Field` with a raw input rather than `TextField`, which cannot
                  take a ref: the header's *Add* focuses this box, and the two
                  controls the discovery-rules form has are built the same way. */}
              <Field
                label="Add a keyword"
                help="A phrase a buyer would write in a post — not a job title."
                error={missing ? 'Type the phrase to add.' : undefined}
              >
                {(aria) => (
                  <input
                    className="ar-input"
                    ref={field}
                    {...aria}
                    value={draft}
                    maxLength={200}
                    placeholder="looking for interior contractor"
                    onChange={(event) => {
                      setDraft(event.target.value)
                      setMissing(false)
                      add.reset()
                    }}
                  />
                )}
              </Field>
            </div>
            {/* Enabled as drawn (parity P-PACK-03); an empty box is refused on
                the press, in the field. */}
            <Button type="submit" tone="secondary" busy={add.pending}>
              Add keyword
            </Button>
          </form>
        ) : null}
        {add.state.failure ? <InlineError failure={add.state.failure} /> : null}
      </div>
    </div>
  )
}

/**
 * What counts as a lead, read-only.
 *
 * Settings consolidation (2026-09-15). The filter card that lived here — hours
 * and phrases with a Save — is `/settings/discovery-rules` now; this says what
 * the rules are and where to change them. `settings/discovery-rules/form.tsx`
 * is the form, used by that panel and by the wizard.
 */
function FilterReadout({ filter }: { filter: Filter }): JSX.Element {
  return (
    <div className="ar-card">
      <div className="ar-card__header">
        <h2>What counts as a lead</h2>
        <span className="ar-card__spacer" />
        <Link className="ar-btn ar-btn--ghost ar-btn--sm" to="/settings/discovery-rules">
          Change in Settings →
        </Link>
      </div>
      <div className="ar-card__body ar-stack">
        <p style={{ margin: 0 }}>{summariseDiscoveryRules(filter)}</p>
        <p className="ar-subtle ar-text-body-sm" style={{ margin: 0 }}>
          Posts whose age we cannot read are always dropped.
        </p>
      </div>
    </div>
  )
}

function PacksCard({
  packs,
  manageable,
  onChanged,
}: {
  packs: Pack[]
  manageable: boolean
  onChanged: () => void
}): JSX.Element {
  const toast = useToast()
  const add = useMutation((slug: string) =>
    call(() => api.POST('/api/v1/outreach/packs/{slug}', { params: { path: { slug } } })),
  )
  const remove = useMutation((slug: string) =>
    call(() => api.DELETE('/api/v1/outreach/packs/{slug}', { params: { path: { slug } } })),
  )

  async function onAdd(pack: Pack): Promise<void> {
    const result = await add.run(pack.slug)
    if (result.ok) {
      const title =
        result.data.keywords_added === 0
          ? `${pack.name} is up to date.`
          : `Added ${String(result.data.keywords_added)} keywords from ${pack.name}.`
      toast.show(
        result.data.exclusions_added > 0
          ? {
              kind: 'success',
              title,
              body: `${String(result.data.exclusions_added)} phrases were added to your skip list.`,
            }
          : { kind: 'success', title },
      )
      onChanged()
    }
  }

  async function onRemove(pack: Pack): Promise<void> {
    const result = await remove.run(pack.slug)
    if (result.ok) {
      toast.show({
        kind: 'info',
        title: `Stopped searching ${pack.name}.`,
        body: 'Its keywords are switched off, not deleted — your leads from them are kept.',
      })
      onChanged()
    }
  }

  const busy = add.pending || remove.pending

  return (
    <div className="ar-card">
      <div className="ar-card__header">
        <h2>Niche keyword packs</h2>
        <span className="ar-card__spacer" />
        {/* OUTR-123. Body-sm, as drawn — a card header's right slot sits beside
            an `h3` and caption size makes it read as a footnote to the heading
            rather than as the other half of it. */}
        <span className="ar-subtle ar-text-body-sm">Curated starting points</span>
      </div>
      <div className="ar-card__body ar-stack ar-stack--tight">
        {/* The API lists only the packs that fit the keywords already held
            (`app/v1/packs._relevant_packs`), so an empty list here means the
            tenant's terms match no vertical in the catalogue — not that the
            catalogue is empty. */}
        {packs.length === 0 ? (
          <Empty
            title="No pack fits what you search for"
            body="The curated packs cover other lines of business. Keep adding your own phrases — suggestions will follow your replies."
          />
        ) : null}
        {packs.map((pack) => {
          const outdated =
            pack.active && pack.installed_version !== null && pack.installed_version < pack.version
          return (
            <div className="app-conn app-packs__row" key={pack.slug}>
              <span className="app-conn__logo" aria-hidden="true">
                {pack.icon}
              </span>
              <span className="app-conn__body">
                <strong>{pack.name}</strong>
                <span>
                  {pack.vertical} · {pack.keyword_count} keywords · {pack.exclusion_count}{' '}
                  exclusions
                </span>
                <span className="ar-subtle" style={{ fontSize: 'var(--ar-text-caption-size)' }}>
                  {pack.summary}
                </span>
              </span>
              {pack.active && !outdated ? (
                <span className="ar-badge ar-badge--success">✓ Active</span>
              ) : null}
              {outdated ? (
                <span className="ar-badge ar-badge--warning">
                  v{pack.version} adds {pack.new_terms}
                </span>
              ) : null}
              {manageable ? (
                <Button
                  tone={pack.active ? 'quiet' : 'ghost'}
                  size="sm"
                  busy={busy}
                  onClick={() => void (pack.active && !outdated ? onRemove(pack) : onAdd(pack))}
                >
                  {outdated ? 'Update' : pack.active ? 'Remove' : 'Add'}
                </Button>
              ) : null}
            </div>
          )
        })}
        {add.state.failure ? <InlineError failure={add.state.failure} /> : null}
        {remove.state.failure ? <InlineError failure={remove.state.failure} /> : null}
      </div>
      <div className="ar-card__footer">
        <span className="ar-subtle" style={{ fontSize: 'var(--ar-text-caption-size)' }}>
          Adding a pack also adds its exclusions to your skip list, where you can edit them.
        </span>
      </div>
    </div>
  )
}

function LastRunCard({ run }: { run: LastRun }): JSX.Element {
  if (run.day === null || run.first_filed_at === null) {
    return (
      <div className="ar-card">
        <div className="ar-card__header">
          <h2>Latest run</h2>
        </div>
        <div className="ar-card__body">
          <Empty
            title="Nothing searched yet"
            body="Once your paired browser runs a search, this shows what each filter dropped and how many leads survived."
          />
        </div>
      </div>
    )
  }

  const rows: { label: string; value: number; note: string; strong?: boolean }[] = [
    {
      label: 'Posts read',
      value: run.posts_read,
      note: `${String(run.keywords_searched)} ${run.keywords_searched === 1 ? 'search' : 'searches'}`,
    },
    {
      label: 'Skipped — no address in the post',
      value: run.no_address,
      note: 'nothing to write to',
    },
    { label: 'Skipped — excluded phrase', value: run.excluded_phrase, note: 'mostly hiring posts' },
    { label: 'Skipped — too old', value: run.too_old, note: 'outside your window' },
    {
      label: 'Skipped — found under another keyword',
      value: run.duplicate,
      note: 'counted once',
    },
    {
      label: 'Skipped — already contacted',
      value: run.already_contacted,
      note: 'never re-contacted',
    },
    { label: 'New leads', value: run.new_leads, note: 'carried an address', strong: true },
  ]

  return (
    <div className="ar-card">
      <div className="ar-card__header">
        <h2>Latest run — {formatDateIn(run.first_filed_at, IST)}</h2>
        <span className="ar-card__spacer" />
        {/* OUTR-127. Body-sm, matching the packs card above it. The heading
            itself stays *Latest run* with the day rather than the drawing's
            *Last run — 04:30 IST*: `discovery_pages` carries no run identifier,
            so a run here is one IST day's filed pages and the time is where that
            day started, not a moment the table can vouch for. */}
        <span className="ar-subtle ar-text-body-sm">
          {/* `formatClock`, not `formatTime`: *from 04:30 IST* without the
              seconds (parity P-PACK-07). The seconds are the reasoning
              trail's, where several steps share a minute. */}
          from {formatClock(run.first_filed_at, IST)} IST
        </span>
      </div>
      <div className="ar-card__body">
        <div className="ar-table-wrap app-packs__runs" tabIndex={0}>
          <table className="ar-table" style={{ background: 'transparent' }}>
            <caption className="ar-visually-hidden">
              What the latest day of discovery read and dropped
            </caption>
            <tbody>
              {rows.map((row) => (
                <tr key={row.label}>
                  <td>{row.strong ? <strong>{row.label}</strong> : row.label}</td>
                  <td className="ar-num">
                    {row.strong ? <strong>{row.value}</strong> : row.value}
                  </td>
                  <td className="ar-num ar-subtle">{row.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
